import React, { useMemo } from 'react';
import { useField } from 'formik';
import styled from 'styled-components';
import { typeography } from '@minecraft.atoms';
import Icon from '@minecraft.icon';

export type InlineSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type InlineSelectInputBaseProps = {
  /** name of the field in the formik state */
  name: string;
  /** list of options available for the user to select from */
  options: InlineSelectOption[];
  /** is the field disabled */
  disabled?: boolean;
  /**
   * side effect when the value changes. Formik already stores the value.
   * Used for things like clearing dependent address fields when the country changes.
   */
  onChange?: (newValue: string) => void;
  /** id of the select. Defaults to `name`. */
  id?: string;
  /** test id value, set on the select */
  'data-testid'?: string;
  /** qa id value, set on the select */
  'data-qa-id'?: string;
  /** Recurly.js hosted-field binding, set on the select */
  'data-recurly'?: string;
};

type InlineSelectAccessibleNameProps =
  | {
      /** visually hidden label. The selected option is the visible text. */
      label: string;
      'aria-label'?: string;
    }
  | {
      label?: string;
      /** accessible name when no label element is rendered */
      'aria-label': string;
    };

export type InlineSelectInputProps = InlineSelectInputBaseProps & InlineSelectAccessibleNameProps;

const toSelectOptions = (options: InlineSelectOption[]): InlineSelectOption[] => {
  if (!Array.isArray(options)) {
    return [];
  }

  const seenValues = new Set<string>();

  return options.filter((option) => {
    if (typeof option?.value !== 'string' || typeof option?.label !== 'string' || seenValues.has(option.value)) {
      return false;
    }

    seenValues.add(option.value);
    return true;
  });
};

const Root = styled.span<{ $disabled: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.designSystem.textColors.disabled : theme.designSystem.textColors.default};
`;

const NativeSelect = styled.select<{ $disabled: boolean }>`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  field-sizing: content;
  box-sizing: border-box;
  width: auto;
  max-width: 100%;
  margin: 0;
  /* 8px gap + 12px chevron, matching the inline country selector */
  padding: 0 1.25rem 0 0;
  border-width: 0;
  border-style: none;
  border-color: transparent;
  border-radius: 0;
  background-color: transparent;
  box-shadow: none;
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.designSystem.textColors.disabled : theme.designSystem.textColors.default};
  font-family: inherit;
  font-size: ${typeography.fontSize.bodySmall};
  font-weight: ${typeography.fontWeight.regular};
  line-height: 1.25rem;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  outline: none;

  &:focus,
  &:focus-visible {
    outline: 4px solid ${({ theme }) => theme.designSystem.borderColors.inputFocusOutline};
  }

  &::-ms-expand {
    display: none;
  }
`;

const ChevronSlot = styled.span`
  position: absolute;
  right: 0;
  display: inline-flex;
  pointer-events: none;
`;

/**
 * Formik select rendered as inline text with a chevron. Opens the browser-native dropdown.
 * Kept separate from SelectInput so boxed field styles are not branched inside that component.
 *
 * Visual example can be seen in _lookbook_/InlineSelectInput
 */
export const InlineSelectInput = ({
  name,
  options,
  disabled = false,
  label,
  onChange: onChangeProp,
  id,
  'aria-label': ariaLabel,
  'data-testid': testId,
  'data-qa-id': qaId,
  'data-recurly': recurlyField,
}: InlineSelectInputProps) => {
  const [field, , helpers] = useField<string>(name);
  const inputId = id ?? name;
  const selectOptions = useMemo(() => toSelectOptions(options), [options]);

  let inputValue = field.value;

  if (inputValue === undefined || inputValue === null) {
    inputValue = '';
  }

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextValue = event.target.value;
    helpers.setValue(nextValue);
    onChangeProp?.(nextValue);
  };

  const handleBlur = (event: React.FocusEvent<HTMLSelectElement>) => {
    field.onBlur(event);
  };

  return (
    <Root $disabled={disabled}>
      {label ? (
        <label className="visually-hidden" htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      <NativeSelect
        $disabled={disabled}
        id={inputId}
        name={name}
        value={inputValue}
        disabled={disabled}
        aria-label={label ? undefined : ariaLabel}
        data-testid={testId}
        data-qa-id={qaId}
        data-recurly={recurlyField}
        onBlur={handleBlur}
        onChange={handleChange}
      >
        {selectOptions.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </NativeSelect>
      <ChevronSlot aria-hidden="true">
        <Icon name="chevron-closed-thin" size="ssm" inheritColor />
      </ChevronSlot>
    </Root>
  );
};
